import {noop, sum, values} from 'lodash'
import {EnvironmentProvider} from 'providers/EnvironmentProvider'
import {ReactNode, createContext, useContext, useEffect, useMemo, useState} from 'react'
import {AppearanceProvider} from './AppearanceProvider'
import {Autocompleter} from './Autocompleter'
import {InputProvider} from './InputProvider'

type Nullable<T> = T | null

const diffCodes = (orderedInputBlocks: StepCode[], a: Nullable<StepCode>, b: Nullable<StepCode>) => {
  const aIndex = orderedInputBlocks.findIndex((item) => item === a)
  const bIndex = orderedInputBlocks.findIndex((item) => item === b)
  return aIndex - bIndex
}

type Props = {
  children: ReactNode
  initialLastPassedStepCode: Nullable<StepCode>
}

export type StepCode = 'guests' | 'availability' | 'option' | 'date'

const useOrderedStepCodes = (): StepCode[] => {
  const stepOrder = AppearanceProvider.useManager().stepOrder
  return useMemo(
    () =>
      stepOrder === 'optionFirst'
        ? ['option', 'guests', 'date', 'availability']
        : ['guests', 'date', 'option', 'availability'],
    [stepOrder]
  )
}

const useNextStepCode = (code: Nullable<StepCode>) => {
  const orderedStepCodes = useOrderedStepCodes()

  if (!code) {
    return orderedStepCodes[0]
  }

  const codeIndex = orderedStepCodes.findIndex((item) => item === code)

  if (codeIndex === -1) {
    throw new Error(`Can't find input block by code '${code}'`)
  }

  const nextIndex = codeIndex + 1

  return nextIndex === orderedStepCodes.length ? null : orderedStepCodes[nextIndex]
}

const useLastCompletedStepCode = (): Nullable<StepCode> => {
  const input = InputProvider.useInput()
  const orderedStepCodes = useOrderedStepCodes()

  const checks = {
    availability: () => input.availabilityId,
    date: () => input.date,
    guests: () => sum(values(input.unitCounters)) > 0,
    option: () => input.optionId,
  }

  for (let i = orderedStepCodes.length - 1; i >= 0; i--) {
    const stepCode = orderedStepCodes[i]

    if (checks[stepCode]()) {
      return stepCode
    }
  }

  return null
}

const useNextStepCodeAfterCompleted = (): Nullable<StepCode> => {
  const lastCompletedStepCode = useLastCompletedStepCode()
  return useNextStepCode(lastCompletedStepCode)
}

type FoldingContextValue = {
  lastPassedStepCode: Nullable<StepCode>
  setLastPassedStepCode: React.Dispatch<React.SetStateAction<Nullable<StepCode>>>
  activeStepCode: Nullable<StepCode>
  setActiveStepCode: React.Dispatch<React.SetStateAction<Nullable<StepCode>>>
}

const FoldingContext = createContext<FoldingContextValue>({
  lastPassedStepCode: null,
  setLastPassedStepCode: noop,
  activeStepCode: null,
  setActiveStepCode: noop,
})

export const FoldingProvider = ({children, initialLastPassedStepCode}: Props) => {
  const input = InputProvider.useInput()
  const orderedStepCodes = useOrderedStepCodes()
  const isCompleted = Autocompleter.useIsCompleted()
  const [activeStepCode, setActiveStepCode] = useState<Nullable<StepCode>>(orderedStepCodes[0])
  const [lastPassedStepCode, setLastPassedStepCode] = useState<Nullable<StepCode>>(initialLastPassedStepCode)
  const lastCompletedStepCode = useLastCompletedStepCode()
  const nextStepCodeAfterCompleted = useNextStepCodeAfterCompleted()

  useEffect(() => {
    if (!isCompleted) {
      return
    }

    if (lastCompletedStepCode) {
      setActiveStepCode(nextStepCodeAfterCompleted)
      setLastPassedStepCode((prevCode) =>
        diffCodes(orderedStepCodes, lastCompletedStepCode, prevCode) < 1 ? prevCode : lastCompletedStepCode
      )
    }
  }, [
    input.unitCounters,
    input.date,
    input.optionId,
    input.availabilityId,
    isCompleted,
    lastPassedStepCode,
    nextStepCodeAfterCompleted,
    lastCompletedStepCode,
    orderedStepCodes,
  ])

  const value = {
    lastPassedStepCode,
    setLastPassedStepCode,
    activeStepCode,
    setActiveStepCode,
  }

  return <FoldingContext.Provider value={value}>{children}</FoldingContext.Provider>
}

FoldingProvider.useOrderedStepCodes = useOrderedStepCodes

const useStepsFoldingContext = () => {
  return useContext(FoldingContext)
}

FoldingProvider.useActiveStepCode = () => {
  const {activeStepCode} = useStepsFoldingContext()
  return activeStepCode
}

FoldingProvider.useActiveStepCodeSetter = () => {
  const {setActiveStepCode} = useStepsFoldingContext()
  return setActiveStepCode
}

FoldingProvider.useAllowEditing = (code: StepCode) => {
  const optionId = EnvironmentProvider.useOptionalOptionId()
  const {lastPassedStepCode} = useStepsFoldingContext()
  const orderedStepCodes = useOrderedStepCodes()

  if (code === 'option' && optionId) {
    return false
  }

  const diff = diffCodes(orderedStepCodes, code, lastPassedStepCode)
  return diff <= 1
}

FoldingProvider.useNextStepCode = useNextStepCode
FoldingProvider.useLastCompletedStepCode = useLastCompletedStepCode
FoldingProvider.useNextStepCodeAfterCompleted = useNextStepCodeAfterCompleted
